import { DuoAuthResponse } from '@/api/DirectDepositApi';
import directDepositRepository from '@/repositories/direct_deposit_repository';
import { appConfig } from '@/config/app';

interface DuoAuthResult {
    success: boolean;
    username?: string;
    action?: string;
    decodedToken?: any;
    error?: string;
    duoCode?: string;
    state?: string;
}

export class DuoService {
    private static instance: DuoService;

    public static getInstance(): DuoService {
        if (!DuoService.instance) {
            DuoService.instance = new DuoService();
        }
        return DuoService.instance;
    }

    /**
     * Initiate Duo Universal authentication
     */
    async initiateAuthentication(action: string, redirectUrl?: string): Promise<DuoAuthResponse> {
        if (!action) {
            throw new Error('Action is required');
        }

        try {
            const response = await directDepositRepository.duo.authCreate({
                action: action,
                redirectUrl: window.location.origin + redirectUrl
            });

            if (response.status !== 200) {
                throw new Error('Failed to initiate Duo authentication');
            }

            return response.data;
        } catch (error) {
            console.error('DuoService: Error initiating authentication:', error);
            throw error;
        }
    }

    /**
     * Check if the current page load is a return from Duo authentication
     */
    isAuthCallback(): boolean {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has('duo_code') && urlParams.has('state') && sessionStorage.getItem('duo_pending_action') !== null;
    }

    /**
     * Explain why a return from Duo can't be used, or return null if it isn't a failed Duo return.
     * Duo sends the user back with `state` plus either `duo_code` (success) or `error`.
     */
    getFailedReturnReason(): string | null {
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has('state') || !(urlParams.has('duo_code') || urlParams.has('error'))) {
            return null;
        }

        if (urlParams.has('error')) {
            return `Duo returned an error: ${urlParams.get('error_description') || urlParams.get('error')}`;
        }

        if (sessionStorage.getItem('duo_pending_action') === null) {
            return 'No pending authentication action found';
        }

        return null;
    }

    /**
     * Forget a Duo attempt that can't be completed
     */
    clearPendingAuth(): void {
        sessionStorage.removeItem('duo_pending_action');
        sessionStorage.removeItem('duo_auth_timestamp');
        sessionStorage.removeItem('duo_redirect_url');
    }

    /**
     * Get URL parameters for Duo callback processing
     */
    getCallbackParams(): URLSearchParams {
        return new URLSearchParams(window.location.search);
    }

    /**
     * Handle authentication callback after redirect from Duo
     */
    async handleAuthCallback(callbackParams: URLSearchParams): Promise<DuoAuthResult> {
        try {
            // Retrieve the stored action and redirectUrl from session storage
            const storedAction = sessionStorage.getItem('duo_pending_action');
            const storedRedirectUrl = sessionStorage.getItem('duo_redirect_url');
            const authTimestamp = sessionStorage.getItem('duo_auth_timestamp');

            if (!storedAction) {
                throw new Error('No pending authentication action found');
            }

            // Check if the authentication attempt is not too old (e.g., 10 minutes)
            if (authTimestamp) {
                const elapsed = Date.now() - parseInt(authTimestamp);
                if (elapsed > 10 * 60 * 1000) {
                    // 10 minutes
                    sessionStorage.removeItem('duo_pending_action');
                    sessionStorage.removeItem('duo_auth_timestamp');
                    throw new Error('Authentication session expired');
                }
            }

            // Extract callback parameters (these depend on your backend implementation)
            const duoCode = callbackParams.get('duo_code');
            const state = callbackParams.get('state');

            if (!duoCode) {
                throw new Error('Missing authentication code from Duo callback');
            }

            // Verify the authentication with your backend
            const response = await directDepositRepository.duo.callbackCreate({
                duoCode: duoCode,
                state: state || '',
                redirectUrl: storedRedirectUrl || undefined
            });

            // Clean up session storage
            sessionStorage.removeItem('duo_pending_action');
            sessionStorage.removeItem('duo_auth_timestamp');
            sessionStorage.removeItem('duo_redirect_url');

            if (response.status === 200 && response.data.success) {
                // Set timestamp for recent authentication (used by router guard)
                sessionStorage.setItem('duo_last_auth_timestamp', Date.now().toString());

                return {
                    success: true,
                    username: response.data.username || 'unknown',
                    action: storedAction,
                    decodedToken: response.data.authResult,
                    duoCode: duoCode,
                    state: state || ''
                };
            } else {
                throw new Error(response.data.error || 'Authentication verification failed');
            }
        } catch (error) {
            console.error('DuoService: Error handling authentication callback:', error);
            // Clean up session storage on error
            sessionStorage.removeItem('duo_pending_action');
            sessionStorage.removeItem('duo_auth_timestamp');
            sessionStorage.removeItem('duo_redirect_url');

            return {
                success: false,
                error: error instanceof Error ? error.message : 'Authentication callback failed'
            };
        }
    }

    /**
     * Complete Duo authentication flow
     */
    async authenticateUser(action: string, redirectUrl?: string): Promise<DuoAuthResult> {
        if (appConfig.duo.mockSuccess) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return {
                success: true,
                username: 'mock-user@mit.edu',
                action: action,
                decodedToken: {
                    sub: 'mock-user@mit.edu',
                    iat: Date.now() / 1000,
                    exp: Date.now() / 1000 + 3600,
                    action: action
                }
            };
        }

        try {
            // Step 1: Initiate authentication
            const authResponse = await this.initiateAuthentication(action, redirectUrl);

            // Step 2: Navigate to Duo authentication URL
            if (authResponse?.authUrl) {
                // Store the action and redirectUrl in session storage for retrieval after redirect
                sessionStorage.setItem('duo_pending_action', action);
                sessionStorage.setItem('duo_auth_timestamp', Date.now().toString());
                if (redirectUrl) {
                    // Store full URL to match what was sent during initiation
                    sessionStorage.setItem('duo_redirect_url', window.location.origin + redirectUrl);
                }

                // Navigate to Duo authentication URL
                window.location.href = authResponse.authUrl;

                // Return a pending state since we're navigating away
                return {
                    success: false,
                    error: 'Authentication in progress - redirecting to Duo'
                };
            } else {
                throw new Error('No authentication URL received from Duo');
            }
        } catch (error) {
            console.error('DuoService: Error in authentication flow:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown authentication error'
            };
        }
    }
}
